/**
 * Event/user detail service helpers
 * Cleanup pass: remove mock participant generation and fake interaction behavior.
 */

import { buildEventsApiUrl } from '../config/api';

export interface User {
  userId?: string;
  userID?: string;
  id?: string;
  userName?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  email?: string;
  location?: string;
  bio?: string;
  avatar?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Item {
  id?: string;
  name?: string;
  description?: string;
  category?: string;
  condition?: string;
  images?: string[];
  ownerName?: string;
  ownerId?: string;
  ownerID?: string;
  createDateTime?: string;
  updateDateTime?: string;
  isActive?: boolean;
  displayName?: string;
}

export interface EventDetailsResponse {
  id: string;
  eventID?: string;
  eventName: string;
  eventDateTime?: string;
  category: string;
  listingCityName: string;
  listingStateName: string;
  description?: string;
  organizerID?: string;
  ownerID?: string;
  maxParticipants?: string | number;
  venueAddress?: string;
  tradeCutoffDate?: string;
  items?: Item[];
  participants?: User[];
  linkedItems?: any[];
  participatingUsers?: string[];
  [key: string]: any;
}

const normalizeEvent = (event: any): EventDetailsResponse => ({
  ...event,
  id: event.id || event.eventID,
  eventID: event.eventID || event.id,
  eventName: event.eventName || 'Unnamed Event',
  eventDateTime: event.eventDateTime,
  category: event.category || 'General',
  listingCityName: event.listingCityName || '',
  listingStateName: event.listingStateName || '',
  description:
    event.description ||
    `Join us for an exciting ${event.category || 'trading'} event in ${event.listingCityName || 'this area'}! Bring your items to trade and meet fellow enthusiasts.`,
  organizerID: event.organizerID || event.ownerID || '',
  ownerID: event.ownerID || event.organizerID || '',
  maxParticipants: event.maxParticipants || '50',
  venueAddress: event.venueAddress || `${event.listingCityName || 'Local'} Community Center`,
  tradeCutoffDate: event.tradeCutoffDate,
  linkedItems: event.linkedItems || [],
  participatingUsers: event.participatingUsers || [],
  participants: event.participants || []
});

const unwrapEventPayload = (payload: any): any | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  if (payload.event && typeof payload.event === 'object') {
    return payload.event;
  }

  if (payload.id || payload.eventID || payload.eventName) {
    return payload;
  }

  return null;
};

const isNotFoundResponse = (response: Response, payload: any): boolean => {
  if (response.status === 404) {
    return true;
  }

  const errorMessage = typeof payload?.error === 'string' ? payload.error.toLowerCase() : '';
  return Boolean(errorMessage && errorMessage.includes('not found'));
};

export const fetchEventDetails = async (eventId: string): Promise<EventDetailsResponse | null> => {
  try {
    console.log('🔍 Fetching event details for:', eventId);

    const directResponse = await fetch(buildEventsApiUrl(`/api/events/${eventId}`));
    const directData = await directResponse.json().catch(() => null);
    const directEvent = unwrapEventPayload(directData);

    if (directResponse.ok && directEvent) {
      const normalized = normalizeEvent(directEvent);
      console.log('✅ Found event via direct endpoint:', normalized.eventName);
      console.log(`📦 REAL ITEMS: ${normalized.linkedItems?.length || 0} items`);
      console.log(`👥 REAL PARTICIPANTS: ${normalized.participatingUsers?.length || 0} ids`);
      return normalized;
    }

    const shouldAttemptLegacyListFallback = isNotFoundResponse(directResponse, directData);

    if (!shouldAttemptLegacyListFallback) {
      if (!directResponse.ok) {
        throw new Error(`HTTP error! status: ${directResponse.status}`);
      }

      console.warn('⚠️ Direct event response had an unexpected shape; skipping all-events fallback', directData);
      return null;
    }

    console.log('🔄 Direct event lookup reported not found, checking legacy list response...');
    const listResponse = await fetch(buildEventsApiUrl('/api/events?timeFilter=all&limit=250&includeDeleted=true'));
    if (!listResponse.ok) {
      throw new Error(`HTTP error! status: ${listResponse.status}`);
    }

    const listData = await listResponse.json();
    const events = Array.isArray(listData?.events) ? listData.events : [];
    const event = events.find((e: any) => e.id === eventId || e.eventID === eventId);

    if (!event) {
      console.log('❌ Event not found in direct or legacy list responses');
      return null;
    }

    const normalized = normalizeEvent(event);
    console.log('✅ Found event in legacy list search:', normalized.eventName);
    console.log(`📦 REAL ITEMS: ${normalized.linkedItems?.length || 0} items`);
    console.log(`👥 REAL PARTICIPANTS: ${normalized.participatingUsers?.length || 0} ids`);
    return normalized;
  } catch (error) {
    console.error('❌ Error fetching event details:', error);
    return null;
  }
};

export const fetchUserProfileSummary = async (userId: string): Promise<User | null> => {
  try {
    const response = await fetch(buildEventsApiUrl(`/api/users/${encodeURIComponent(userId)}/profile`));
    const data = await response.json();
    if (!response.ok || !data?.profile) return null;
    return data.profile;
  } catch (error) {
    console.error('? Error fetching user profile summary:', error);
    return null;
  }
};

const defaultExport = {
  fetchEventDetails,
  fetchUserProfileSummary
};

export default defaultExport;
